import { useState, useEffect } from "react";

import {
  Close as CloseIcon,
  Translate as TranslateIcon,
  Save as SaveIcon,
  AutoAwesome as AutoAwesomeIcon,
} from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Chip,
  Divider,
  CircularProgress,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Paper,
} from "@mui/material";
import { getSurveyTranslations } from "@rubi/api-generated/clientRs/sdk.gen";

import {
  REPORT_LANGUAGES_MAP,
  languageCodeToTranslateLocale,
} from "@/common/constants/languages";

export interface ItemTranslation {
  [languageCode: string]: string;
}

interface BulkTranslationModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (translations: { [itemName: string]: ItemTranslation }) => void;
  items: Array<{ item_name: string; item_code: string }>;
  existingTranslations?: { [itemName: string]: ItemTranslation };
  loading?: boolean;
  supportedLanguages?: string[]; // Agency's supported languages
}

const BulkTranslationModal: React.FC<BulkTranslationModalProps> = ({
  open,
  onClose,
  onSave,
  items,
  existingTranslations = {},
  loading = false,
  supportedLanguages,
}) => {
  // Filter languages based on agency's supported languages
  // Exclude English variants (enCa, enUs) as we translate FROM English TO other languages
  // If supportedLanguages is provided, use REPORT_LANGUAGES_MAP filtered by it
  // Otherwise, fall back to all languages from REPORT_LANGUAGES_MAP
  const availableLanguages = (
    supportedLanguages
      ? REPORT_LANGUAGES_MAP.filter((lang) =>
          supportedLanguages.includes(lang.code),
        )
      : REPORT_LANGUAGES_MAP
  ).filter((lang) => lang.code !== "enCa" && lang.code !== "enUs");
  const [translations, setTranslations] = useState<{
    [itemName: string]: ItemTranslation;
  }>({});
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [activeLanguage, setActiveLanguage] = useState<string | null>(null);
  const [autoTranslating, setAutoTranslating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Initialize translations when modal opens
  useEffect(() => {
    if (open) {
      setTranslations({ ...existingTranslations });

      // Pre-select languages that have existing translations
      const existingLanguages = new Set<string>();
      Object.values(existingTranslations).forEach((itemTranslations) => {
        Object.keys(itemTranslations).forEach((lang) => {
          existingLanguages.add(lang);
        });
      });
      const langs = Array.from(existingLanguages).sort();
      setSelectedLanguages(langs);
      setActiveLanguage(langs[0] ?? null);

      setErrors({});
    }
  }, [open, existingTranslations]);

  // Keep the active tab pointing at a valid selected language
  useEffect(() => {
    if (activeLanguage && !selectedLanguages.includes(activeLanguage)) {
      setActiveLanguage(selectedLanguages[0] ?? null);
    } else if (!activeLanguage && selectedLanguages.length > 0) {
      setActiveLanguage(selectedLanguages[0]);
    }
  }, [selectedLanguages]);

  const handleLanguageToggle = (languageCode: string) => {
    setSelectedLanguages((prev) =>
      prev.includes(languageCode)
        ? prev.filter((lang) => lang !== languageCode)
        : [...prev, languageCode],
    );
  };

  const handleSelectAllLanguages = () => {
    const allLanguageCodes = availableLanguages.map((lang) => lang.code);
    setSelectedLanguages(allLanguageCodes);
  };

  const handleDeselectAllLanguages = () => {
    setSelectedLanguages([]);
  };

  const handleTranslationChange = (
    itemName: string,
    languageCode: string,
    value: string,
  ) => {
    setTranslations((prev) => ({
      ...prev,
      [itemName]: {
        ...prev[itemName],
        [languageCode]: value,
      },
    }));

    // Clear error for this field
    const errorKey = `${itemName}-${languageCode}`;
    if (errors[errorKey]) {
      setErrors((prev) => ({
        ...prev,
        [errorKey]: "",
      }));
    }
  };

  const handleAutoTranslate = async (languageCode: string) => {
    setAutoTranslating(true);
    try {
      const stringsToTranslate = items.map((item) => item.item_name);

      const response = await getSurveyTranslations({
        body: {
          source_strings: stringsToTranslate.map((text) => ({ Text: text })),
          target_language: languageCodeToTranslateLocale(languageCode),
        },
      });

      if (response.data?.translations) {
        const newTranslations = { ...translations };

        items.forEach((item, index) => {
          const translation = response.data.translations[index];
          if (translation?.translations?.[0]?.text) {
            // Only create the translation entry if we have a valid text
            if (!newTranslations[item.item_name]) {
              newTranslations[item.item_name] = {};
            }
            newTranslations[item.item_name][languageCode] =
              translation.translations[0].text;
          }
        });

        setTranslations(newTranslations);
      }
    } catch (error) {
      console.error("Auto-translation failed:", error);
      setErrors((prev) => ({
        ...prev,
        [`auto-translate-${languageCode}`]:
          "Auto-translation failed. Please try again.",
      }));
    } finally {
      setAutoTranslating(false);
    }
  };

  const handleAutoTranslateAll = async () => {
    if (selectedLanguages.length === 0) return;

    setAutoTranslating(true);
    try {
      const stringsToTranslate = items.map((item) => item.item_name);
      const newTranslations = { ...translations };

      // Translate to all selected languages
      for (const languageCode of selectedLanguages) {
        try {
          const response = await getSurveyTranslations({
            body: {
              source_strings: stringsToTranslate.map((text) => ({
                Text: text,
              })),
              target_language: languageCodeToTranslateLocale(languageCode),
            },
          });

          if (response.data?.translations) {
            items.forEach((item, index) => {
              const translation = response.data.translations[index];
              if (translation?.translations?.[0]?.text) {
                // Only create the translation entry if we have a valid text
                if (!newTranslations[item.item_name]) {
                  newTranslations[item.item_name] = {};
                }
                newTranslations[item.item_name][languageCode] =
                  translation.translations[0].text;
              }
            });
          }
        } catch (error) {
          console.error(`Auto-translation failed for ${languageCode}:`, error);
          setErrors((prev) => ({
            ...prev,
            [`auto-translate-${languageCode}`]: `Auto-translation failed for ${languageCode}. Please try again.`,
          }));
        }
      }

      setTranslations(newTranslations);
    } catch (error) {
      console.error("Auto-translation failed:", error);
    } finally {
      setAutoTranslating(false);
    }
  };

  const handleSave = async () => {
    // Validate translations and filter out null/undefined values
    const newErrors: { [key: string]: string } = {};
    const cleanedTranslations: { [itemName: string]: ItemTranslation } = {};

    Object.entries(translations).forEach(([itemName, itemTranslations]) => {
      if (itemTranslations && typeof itemTranslations === "object") {
        const cleanedItemTranslations: ItemTranslation = {};

        Object.entries(itemTranslations).forEach(([lang, translation]) => {
          // Only include languages that are currently selected (deselecting removes from payload)
          if (!selectedLanguages.includes(lang)) return;

          // Check for null, undefined, or empty translations
          if (translation == null || translation === undefined) {
            newErrors[`${itemName}-${lang}`] =
              "Translation cannot be null or undefined";
          } else if (typeof translation !== "string") {
            newErrors[`${itemName}-${lang}`] = "Translation must be a string";
          } else if (translation.trim() === "") {
            newErrors[`${itemName}-${lang}`] = "Translation cannot be empty";
          } else {
            // Only include valid translations
            cleanedItemTranslations[lang] = translation;
          }
        });

        // Only include items that have valid translations
        if (Object.keys(cleanedItemTranslations).length > 0) {
          cleanedTranslations[itemName] = cleanedItemTranslations;
        }
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSaving(true);
    try {
      await onSave(cleanedTranslations);
      onClose();
    } catch (error) {
      console.error("Failed to save translations:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving && !autoTranslating) {
      onClose();
    }
  };

  const getLanguageDisplayName = (languageCode: string) => {
    const lang = REPORT_LANGUAGES_MAP.find((l) => l.code === languageCode);
    return lang ? `${lang.name}` : languageCode;
  };

  // Check if a language has existing translations from when the modal was opened
  const languageHasExistingTranslations = (languageCode: string): boolean => {
    return Object.values(existingTranslations).some(
      (itemTranslations) =>
        itemTranslations[languageCode] &&
        itemTranslations[languageCode].trim() !== "",
    );
  };

  // Get count of existing languages
  const existingLanguageCount = availableLanguages.filter((lang) =>
    languageHasExistingTranslations(lang.code),
  ).length;

  const hasNonEmptyTranslations = Object.values(translations).some(
    (itemTranslations) =>
      Object.values(itemTranslations).some((t) => t.trim() !== ""),
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: "80vh" },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TranslateIcon color="primary" />
          <Typography variant="h6">Bulk Translation</Typography>
        </Box>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
          Translate all item names to multiple languages at once
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            Select languages and use auto-translation or enter translations
            manually.
          </Typography>

          {/* Language Selection */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="subtitle2">Select Languages:</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSelectAllLanguages}
                  disabled={
                    selectedLanguages.length === availableLanguages.length
                  }
                >
                  Select All
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleDeselectAllLanguages}
                  disabled={selectedLanguages.length === 0}
                >
                  Deselect All
                </Button>
              </Box>
            </Box>
            {existingLanguageCount > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                This list already has translations in {existingLanguageCount}{" "}
                language
                {existingLanguageCount !== 1 ? "s" : ""}. Languages with
                existing translations are highlighted with a checkmark.
              </Alert>
            )}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {availableLanguages.map((language) => {
                const hasExisting = languageHasExistingTranslations(
                  language.code,
                );
                const isSelected = selectedLanguages.includes(language.code);
                return (
                  <Chip
                    key={language.code}
                    label={
                      hasExisting
                        ? `✓ ${getLanguageDisplayName(language.code)}`
                        : getLanguageDisplayName(language.code)
                    }
                    clickable
                    color={
                      isSelected
                        ? "primary"
                        : hasExisting
                          ? "success"
                          : "default"
                    }
                    variant={isSelected ? "filled" : "outlined"}
                    onClick={() => handleLanguageToggle(language.code)}
                    sx={{ mb: 1 }}
                  />
                );
              })}
            </Box>
          </Box>

          {/* Auto-translate buttons */}
          {selectedLanguages.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={
                    autoTranslating ? (
                      <CircularProgress size={16} />
                    ) : (
                      <AutoAwesomeIcon />
                    )
                  }
                  onClick={handleAutoTranslateAll}
                  disabled={autoTranslating}
                >
                  Auto-translate All Languages
                </Button>
                {activeLanguage && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={
                      autoTranslating ? (
                        <CircularProgress size={16} />
                      ) : (
                        <AutoAwesomeIcon />
                      )
                    }
                    onClick={() => handleAutoTranslate(activeLanguage)}
                    disabled={autoTranslating}
                  >
                    Auto-translate to {getLanguageDisplayName(activeLanguage)}
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Translation Table */}
        {selectedLanguages.length > 0 && activeLanguage && (
          <>
            <Tabs
              value={activeLanguage}
              onChange={(_, val) => setActiveLanguage(val)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
            >
              {selectedLanguages.map((langCode) => (
                <Tab
                  key={langCode}
                  label={getLanguageDisplayName(langCode)}
                  value={langCode}
                />
              ))}
            </Tabs>
            <TableContainer
              component={Paper}
              sx={{ maxHeight: 600, overflow: "auto" }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 200, fontWeight: "bold" }}>
                      Item Name
                    </TableCell>
                    <TableCell sx={{ minWidth: 300, fontWeight: "bold" }}>
                      {getLanguageDisplayName(activeLanguage)}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => {
                    const errorKey = `${item.item_name}-${activeLanguage}`;
                    const hasError = !!errors[errorKey];
                    return (
                      <TableRow key={item.item_name}>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "medium" }}
                          >
                            {item.item_name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "text.secondary" }}
                          >
                            Code: {item.item_code}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            value={
                              translations[item.item_name]?.[activeLanguage] ||
                              ""
                            }
                            onChange={(e) =>
                              handleTranslationChange(
                                item.item_name,
                                activeLanguage,
                                e.target.value,
                              )
                            }
                            placeholder="Enter translation..."
                            variant="outlined"
                            size="small"
                            error={hasError}
                            helperText={hasError ? errors[errorKey] : ""}
                            multiline
                            rows={2}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {Object.keys(errors).length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Please fix the errors above before saving.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={saving || autoTranslating}
          startIcon={<CloseIcon />}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={
            saving ||
            autoTranslating ||
            !hasNonEmptyTranslations ||
            selectedLanguages.length === 0
          }
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
        >
          {saving ? "Saving..." : "Save All Translations"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkTranslationModal;
